import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BACK_TO_SCHOOL_CAMPAIGN_DURATION } from '@features/backToSchoolCampaign/constants/backToSchoolCampaignDuration'
import { useBackToSchoolCampaign } from '@features/backToSchoolCampaign/hooks/useBackToSchoolCampaign'
import { isFreeAudience } from '@features/backToSchoolCampaign/utils/isFreeAudience'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useIsOrganizationOwner } from '@modules-deprecated/app/organization/hooks/useIsOrganizationOwner'
import { resolveSubscriptionPlan } from '@views/settings/routes/OrganizationSubscriptionSettings/utils/resolveSubscriptionPlan'
import { isSettingsModulePath } from '@views/settings/utils/isSettingsModulePath'
import { routeToInternalSettings } from '@views/settings/utils/routeToInternalSettings'

import { EmberRoute } from '../../../../../../../enums/emberRoute'
import { Scope } from '../../../../../../../enums/scope'
import { useOrganizationMarket, useOrganizationSubscription } from '../../../../../../../hooks'
import { useEmberRouteUrl } from '../../../../../../../hooks/routing/useEmberRouteUrl'
import { useEmberTransitionTo } from '../../../../../../../hooks/routing/useEmberTransitionTo'
import { isAuthorized } from '../../../../../../../utils/isAuthorized'

export const useTrialStatusBanner = () => {
  const { t } = useTranslation()
  const { transitionTo } = useEmberTransitionTo()
  const { url: settingsSubscriptionsPlansSelectionUrl } = useEmberRouteUrl(
    EmberRoute.SettingsSubscriptionsPlansSelection,
  )
  const {
    isLoading: isLoadingSubscription,
    isLocked,
    isTrialNoPlanSelected,
    remainingTrialDays,
    totalTrialDays,
  } = useOrganizationSubscription()
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { isOrganizationOwner } = useIsOrganizationOwner()
  const { market } = useOrganizationMarket()
  const {
    audience,
    isCampaignActive,
    remainingCampaignDays,
    trackCampaignEntryPointClicked,
    trackCampaignEntryPointViewed,
  } = useBackToSchoolCampaign()

  const isLoading = isLoadingSubscription || isLoadingOrganization
  const isSubscriptionPaidByAccountant = !!organization?.consolidatedBilling
  const canManageSubscription = isAuthorized(Scope.OrganizationSubscriptionWrite, false, isOrganizationOwner)

  const isTrialExpired = remainingTrialDays !== null && remainingTrialDays < 1
  const isTrialPlanActive =
    isTrialNoPlanSelected && !isTrialExpired && remainingTrialDays !== null && totalTrialDays !== null

  const isFreePlanAudience = isFreeAudience(audience)

  const shouldBeVisible =
    !isLoading &&
    (isFreePlanAudience || (isTrialPlanActive && !isLocked && !isSubscriptionPaidByAccountant && isOrganizationOwner))

  const progressValue = useMemo(() => {
    if (isCampaignActive && remainingCampaignDays) {
      return Math.min((remainingCampaignDays / BACK_TO_SCHOOL_CAMPAIGN_DURATION) * 100, 100)
    }

    if (remainingTrialDays && totalTrialDays) {
      return Math.min(Math.max((remainingTrialDays / totalTrialDays) * 100, 0), 100)
    }

    return 0
  }, [isCampaignActive, remainingCampaignDays, remainingTrialDays, totalTrialDays])

  const progressLabel = useMemo(() => {
    if (remainingTrialDays !== null && !isCampaignActive) {
      return t('navigation.menu.footer.trial_status_banner.days_left', { count: remainingTrialDays })
    }

    if (isCampaignActive && remainingCampaignDays) {
      return t('navigation.menu.footer.trial_status_banner.campaign_days_left', { count: remainingCampaignDays })
    }

    return ''
  }, [isCampaignActive, remainingCampaignDays, remainingTrialDays, t])

  const subscriptionPlanName = useMemo(() => {
    if (!organization?.subscriptionProductPlan) {
      return ''
    }
    const {
      content: { planName },
    } = resolveSubscriptionPlan(organization.subscriptionProductPlan, market || undefined)

    return t(planName ?? '')
  }, [market, organization?.subscriptionProductPlan, t])

  const headerLabel = useMemo(() => {
    if (isCampaignActive && isFreePlanAudience) {
      return t('back_to_school.status_banner.header.free')
    }

    return subscriptionPlanName
  }, [isFreePlanAudience, isCampaignActive, subscriptionPlanName, t])

  const navigateToSubscriptionPlanSelection = useCallback(() => {
    if (isSettingsModulePath()) {
      routeToInternalSettings(EmberRoute.SettingsSubscriptionsPlansSelection)
      return
    }

    if (settingsSubscriptionsPlansSelectionUrl) {
      transitionTo(settingsSubscriptionsPlansSelectionUrl)
    }
  }, [settingsSubscriptionsPlansSelectionUrl, transitionTo])

  return {
    canManageSubscription,
    headerLabel,
    isBackToSchoolCampaignActive: isCampaignActive,
    isFreePlanAudience,
    navigateToSubscriptionPlanSelection,
    progressLabel,
    progressValue,
    shouldBeVisible,
    subscriptionPlanName,
    trackCampaignEntryPointClicked,
    trackCampaignEntryPointViewed,
  }
}
